import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { MagnifyingGlass, Package, X } from "@phosphor-icons/react";

const fmtM = (n) => n ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2 })}` : "$0.00";

export default function ProductSearchInput({ token, value, onSelect, onManualChange, placeholder }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(q)}&limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(data.products || []);
    } catch { setResults([]); }
    setLoading(false);
  }, [token]);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onManualChange(v);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const handleSelect = (product) => {
    setQuery(product.name);
    setOpen(false);
    setResults([]);
    onSelect(product);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder || "Buscar producto o escribir manualmente..."}
          className="w-full px-3 py-2 pl-8 border rounded-lg text-sm font-medium"
          data-testid="product-search-input"
        />
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-[300px] overflow-y-auto" data-testid="product-search-results">
          {results.map(p => (
            <button key={p.id} onClick={() => handleSelect(p)}
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center gap-3 border-b last:border-0 transition-colors">
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                <Package size={14} className="text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                <div className="flex items-center gap-2 text-[10px] text-slate-400">
                  <span className="font-mono text-blue-600">{p.sku}</span>
                  <span>·</span>
                  <span>PL: {fmtM(p.precio_lista)}</span>
                  <span>·</span>
                  <span className={p.stock_qty > 0 ? "text-emerald-600 font-bold" : "text-red-500 font-bold"}>
                    {p.stock_qty > 0 ? `${p.stock_qty} en stock` : "Sin stock"}
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold text-emerald-600">{fmtM(p.precio_lista)}</p>
                <p className="text-[10px] text-slate-400">{fmtM(p.precio_sin_costo)} PSC</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl p-4 text-center">
          <p className="text-xs text-slate-400">Sin resultados. Escribe manualmente.</p>
        </div>
      )}
    </div>
  );
}
